<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { maskCurrency } from "@/common/utils/mask";

import type {
	PlanPriceType,
	PlanPricePayloadType,
} from "@/common/types/plan/price";

import { baseInput, baseSelect, baseTextarea } from "@/common/components";

const { t, tm } = useI18n();

const emit = defineEmits(["update:data", "validate"]);

const props = defineProps<{
	data: PlanPriceType | null;
	isPatch?: boolean;
	isEdit?: boolean;
}>();

const DEFAULT_DATA = {
	stripe_price_id: "",
	type: "PLAN",
	name: "",
	currency: "BRL",
	frequency: "MONTHLY",
	status: "ACTIVE",
	price: 0,
	description: null,
};

const payload = ref<PlanPricePayloadType>({ ...DEFAULT_DATA });
const errors = ref<{ [key: string]: string }>({});

const mounted = ref(0);

const statusOptions = computed(() => [
	{ option: t("common.labels.active"), value: "ACTIVE" },
	{ option: t("common.labels.inactive"), value: "INACTIVE" },
]);

const typeOptions = computed(() => [
	{ option: "Plano", value: "PLAN" },
	{ option: "Affiliado", value: "AFFILIATE" },
]);

const frequencyOptions = computed(() => [
	{ option: "Mensal", value: "MONTHLY" },
	{ option: "Semestral", value: "SEMESTER" },
	{ option: "Anual", value: "YEARLY" },
]);

const currencyOptions = computed(() =>
	Object.entries(tm("enums.currencies") as Record<string, string>).map(
		([value, option]) => ({
			option,
			value,
		}),
	),
);

function validate() {
	const free = ["description"];

	Object.keys(payload.value).forEach((key) => {
		delete errors.value[key];

		if (!payload.value[key] && !free.includes(key))
			errors.value[key] = t("error.required");
	});

	console.log("errors", errors.value);

	emit("validate", Object.keys(errors.value).length === 0);
}

function update(key: keyof typeof payload.value) {
	validate();

	if (props.isPatch) {
		if (payload.value[key] === props.data[key]) return;

		emit("update:data", { [key]: payload.value[key] });
		return;
	}

	emit("update:data", payload.value);
}

function resumeData() {
	if (!props.data) return;

	const {
		stripe_price_id,
		currency,
		name,
		type,
		status,
		price: priceDecimal,
		frequency,
		description,
	} = props.data;

	const priceFloat = Number.parseFloat(priceDecimal?.toString()).toFixed(2);
	const priceString = maskCurrency(priceFloat, currency as any);

	payload.value = {
		stripe_price_id,
		currency,
		name,
		type,
		status,
		price: priceString as any,
		frequency,
		description,
	};

	mounted.value += 1;
}

function maskMoney(data: number) {
	return maskCurrency(data, payload.value.currency as any);
}

function changeCurrency() {
	payload.value.price = maskMoney(payload.value.price) as any;

	emit("update:data", {
		price: payload.value.price,
		currency: payload.value.currency,
	});
}

watch(
	() => mounted.value,
	() => {
		if (mounted.value === 1) {
			emit("update:data", payload.value);
		}
	},
);

watch(
	() => props.data,
	() => {
		resumeData();
	},
	{ immediate: true },
);
</script>

<template>
	<article class="form-plan">
		<section class="group-name">
			<base-input
				v-model="payload.name"
				:value="payload.name"
				:placeholder="t('common.placeholders.name')"
				required
				maxlength="75"
				@blur="update('name')"
			>
				<template #label> {{ t("common.labels.name") }} *</template>
				<template v-if="errors.name" #error> {{ errors.name }} </template>
			</base-input>

			<base-select
				v-model="payload.status"
				:value="payload.status"
				:options="statusOptions"
				:is-empty="false"
				@change="update('status')"
			>
				<template #label> {{ t("common.labels.status") }}</template>
			</base-select>
		</section>

		<base-input
			v-model="payload.stripe_price_id"
			:value="payload.stripe_price_id"
			@blur="update('stripe_price_id')"
		>
			<template #label> Stripe Preço ID *</template>
			<template v-if="errors.stripe_price_id" #error>
				{{ errors.stripe_price_id }}
			</template>
		</base-input>

		<section class="flex flex-row gap-4">
			<base-select
				v-model="payload.type"
				:value="payload.type"
				:options="typeOptions"
				:is-empty="false"
				@change="update('type')"
			>
				<template #label> {{ t("common.labels.type") }}</template>
			</base-select>

			<base-select
				v-model="payload.frequency"
				:value="payload.frequency"
				:options="frequencyOptions"
				:is-empty="false"
				@change="update('frequency')"
			>
				<template #label> Frequência *</template>
			</base-select>
		</section>

		<section class="flex flex-row gap-4">
			<base-select
				v-model="payload.currency"
				:value="payload.currency"
				:options="currencyOptions"
				:is-empty="false"
				@change="changeCurrency()"
			>
				<template #label> {{ t("common.labels.currency") }}</template>
			</base-select>

			<base-input
				v-model="payload.price"
				:value="payload.price"
				:mask="maskMoney"
				@blur="update('price')"
			>
				<template #label> {{ t("common.labels.price") }} *</template>
				<template v-if="errors.price" #error> {{ errors.price }} </template>
			</base-input>
		</section>

		<base-textarea
			v-model="payload.description"
			:value="payload.description"
			:rows="5"
			@blur="update('description')"
		>
			<template #label>
				{{ t("common.labels.description") }}
			</template>
		</base-textarea>
	</article>
</template>

<style lang="scss" scoped>
.form-plan {
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 1rem;

	.group-name {
		display: grid;
		grid-template-columns: 2fr 1fr;
		gap: 1rem;
	}
}
</style>
